import React, { useEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import tw from 'twin.macro';

import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import {
  Button,
  Grid,
  Section,
  Col,
  Flex,
  Overlay,
  SanityBlockContent,
  Form,
} from '../../common';

const StyledHeroHeadline = styled(motion.h1)`
  color: ${(props) => props.$headline};
  ${tw`mb-10`};
`;

const StyledHeroSubheadline = styled(motion.div)`
  color: ${(props) => props.$subheadline};
  & a {
    color: ${(props) => props.$aColor} !important;
    &:hover {
      color: ${(props) => props.$aColorHover} !important;
    }
  }
`;

const HeroForm = (props) => {
  const { sanity, page } = props;
  const { _rawHeroSubheadline, heroBgImg, heroHeadline } = page;
  const { colors, form } = sanity;

  const variants = {
    hidden: { opacity: 0, scale: 0.8, x: -100 },
    visible: { opacity: 1, scale: 1, x: 0 },
  };

  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
    if (!inView) {
      controls.start('hidden');
    }
  }, [controls, inView]);

  return (
    <Section
      ref={ref}
      padding="custom"
      idName="hero-form"
      classes="relative flex flex-col place-content-center place-items-center pt-[8rem] lg:pt-[14rem] pb-[7rem]"
    >
      <div className="absolute inset-0">
        <GatsbyImage
          image={heroBgImg?.asset?.gatsbyImageData}
          alt={heroBgImg?.alt}
          className="h-full w-full object-cover"
        />
        <Overlay
          isGradient={colors?.hero?.isGradient}
          isAngled={colors?.hero?.isAngled}
          isDiamond={colors?.hero?.isDiamond}
          bgColorAngle={colors?.hero?.bgColorAngle}
          bgColorWidth={colors?.hero?.bgColorWidth}
          bgColorHeight={colors?.hero?.bgColorHeight}
          bgColor={colors?.hero?.overlayImage}
          bgColorHover={colors?.neutral?.dark}
          bgColorStart={colors?.neutral?.darker}
          bgColorMid={colors?.neutral?.dark}
          bgColorEnd={colors?.neutral?.default}
        />
      </div>
      <Grid classes="lg:grid-cols-5 gap-y-16 lg:gap-24 max-w-7xl place-items-center">
        <Col classes="order-1 lg:col-span-3 p-4 lg:p-0 relative space-y-8">
          <StyledHeroHeadline
            className="text-center lg:text-left"
            $headline={colors?.neutral?.white}
          >
            <span className="drop-shadow-headline-lg !text-center lg:text-left">
              {heroHeadline}
            </span>
          </StyledHeroHeadline>
          <StyledHeroSubheadline
            className="space-y-7 text-left text-lg font-medium sm:text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
            $subheadline={colors?.neutral?.white}
            $aColor={colors?.accent?.light}
            $aColorHover={colors?.accent?.lighter}
          >
            <SanityBlockContent sanity={sanity} blocks={_rawHeroSubheadline} />
          </StyledHeroSubheadline>
        </Col>
        <Col classes="order-2 lg:col-span-2 p-2 lg:p-0 relative">
          <div className="overflow-hidden">
            <Form formName="form-hero" page={page} sanity={sanity} />
          </div>
        </Col>
      </Grid>
    </Section>
  );
};

export { HeroForm };
